<template>
  <div v-if="topic && topic.settings.status === TOPIC_STATUS_BLOCKED">
    <div
      class="anz-align-center anz-my-xl anz-bg-orange-light anz-py-m anz-pl-l anz-pr-s anz-b-rounded"
    >
      <h6 class="anz-h6 anz-font-bold">
        {{ $translate('post.texts.topicBlocked') }}
      </h6>
    </div>
  </div>
  <div
    v-else
    class="anz-bg-black-3 anz-p-m anz-mt-s anz-posts__item-create anz-b-rounded"
  >
    <div>
      <div v-if="apiError">
        <div>
          {{ apiError.message }}
        </div>
      </div>
      <div v-else>
        <div class="anz-mb-s">
          <div class="anz-text-break-word" v-if="savingPost">
            {{ newPost.content }}
          </div>
          <div class="anz-fc-row anz-fc-row-emoji anz-fc-row-icon" v-else>
            <post-text
              v-model="newPost.content"
              :placeholder="
                currentUser.nickname +
                  $translate('post.forms.textareaPlaceholder')
              "
              :has-error="
                validationError && validationError.hasError('content')
              "
              :error-messages="
                validationError ? validationError.getError('content') : []
              "
              :saving-post="savingPost"
            />
          </div>
        </div>
        <div
          class="anz-flex-sm anz-align-items-center anz-justify-content-between"
        >
          <div>
            <a-checkbox
              class="anz-mb-xs anz-color-gray-700 anz-fs-xxs"
              :title="$translate('post.forms.subscribeTopic')"
              v-model="subscribeTopic"
              data-cy="post-create-input-subscribe-topic"
            >
            </a-checkbox>
            <a-checkbox
              class="anz-color-gray-700 anz-fs-xxs"
              :title="$translate('post.forms.subscribePost')"
              v-model="newPost.subscribe"
              data-cy="post-create-input-subscribe-post"
            >
            </a-checkbox>
          </div>
          <div class="anz-mt-m anz-mt-0-sm anz-ml-m-sm anz-flex">
            <a-button
              v-if="allowClose"
              class="anz-btn-border anz-mr-s anz-post__item-create-close"
              @click="$emit('tooltip-close')"
              :title="$translate('post.actions.closeButton')"
            />
            <a-button
              :title="$translate('post.actions.respondButton')"
              class-name="anz-btn anz-btn-primary anz-wf"
              @click.prevent="createPost"
              :disabled="
                savingPost || CONTENT_MAX_LENGTH < newPost.content.length
              "
              :loading="savingPost"
              data-cy="post-create-respond"
              icon-width="1.0714em"
              icon-height="1.0714em"
              icon-class="anz-mr-s"
              loader-class="anz-spinner-xs"
            >
            </a-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, Ref, ref, toRefs } from 'vue'
import AButton from '@/components/Forms/AButton.vue'
import useCurrentUser from '@/composables/user/user'
import usePostApi from '@/composables/post/postApi'
import { Post } from '@/model/data-object/Post'
import usePostFactory from '@/composables/post/postFactory'
import { NewPost } from '@/model/data-object/NewPost'
import validatePost, {
  PostContentConstraints,
} from '@/validation/postValidator'
import ValidationError from '@/error/validationError'
import ApiValidationError from '@/error/apiValidationError'
import ACheckbox from '@/components/Forms/ACheckbox.vue'
import { TopicNotification } from '@/model/data-object/TopicNotification'
import useConfiguration from '@/composables/configuration/configuration'
import ApiError from '@/error/apiError'
import useAppState from '@/composables/ui/ui'
import { TopicStatus } from '@/model/data-object/TopicSettings'
import useTopic from '@/composables/topic/topic'
import PostText from '@/components/Forms/PostText.vue'
import useAnalyticsManager, {
  ANALYTICS_EVENT_POST_NEW, ANALYTICS_EVENT_POST_RESPOND,
} from '@/composables/analytics/analyticsManager'
import useUiFactory from '@/composables/ui/uiFactory'
import usePostsLoader from '@/composables/post/postLoader'

export default defineComponent({
  name: 'PostCreate',
  emits: ['created-post'],
  components: { PostText, ACheckbox, AButton },
  props: {
    parentPost: {
      type: Object,
      required: false,
    },
    title: {
      type: String,
      required: false,
    },
    defaultTitle: {
      type: Boolean,
      default: true,
    },
    allowClose: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      CONTENT_MAX_LENGTH: PostContentConstraints.MAX_LENGTH,
      TOPIC_STATUS_BLOCKED: TopicStatus.BLOCKED,
      TOPIC_STATUS_ACTIVE: TopicStatus.ACTIVE,
      TOPIC_STATUS_DELETED: TopicStatus.DELETED,
    }
  },
  setup(props, context) {
    const postContentRef = ref(null)
    const { configuration } = useConfiguration()
    const { currentUser } = useCurrentUser()
    const { initPost } = usePostFactory()
    const { lastSavedPost, postIdToShow } = useAppState()
    const newPost: Ref<NewPost | undefined> = ref<NewPost>(
      initPost(props.parentPost as Post)
    )
    const postState = reactive({
      savingPost: false,
    })
    const validationError: Ref<ValidationError | undefined> = ref()
    const apiError: Ref<ApiError | undefined> = ref()
    const subscribeTopic: Ref<boolean | undefined> = ref(false)
    const subscribePost: Ref<boolean | undefined> = ref(false)
    const { topic } = useTopic()
    const { removeRespondHash } = useUiFactory()
    const { addPost } = usePostsLoader()

    const createPost = () => {
      if (subscribeTopic.value) {
        newPost.value?.notifications.push(
          new TopicNotification(
            props.parentPost
              ? props.parentPost.topic.id
              : configuration.widget.topicId
          )
        )
      }

      try {
        validatePost(newPost.value as NewPost)
        const { create } = usePostApi()
        postState.savingPost = true
        create(newPost.value as NewPost)
          .then((post: Post) => {
            const { propagateAnalyticsEvent } = useAnalyticsManager()
            propagateAnalyticsEvent(post, ANALYTICS_EVENT_POST_NEW)
            lastSavedPost.value = post
            postIdToShow.value = null
            context.emit('created-post', post)
            newPost.value = initPost(props.parentPost as Post)
            removeRespondHash()

            if (post.parent) {
              addPost(post)
            }
          })
          .catch((err: Error) => {
            if (err instanceof ApiValidationError) {
              validationError.value = err
            } else if (err instanceof ApiError) {
              apiError.value = err
            }
          })
          .finally(() => {
            postState.savingPost = false
            validationError.value = undefined
          })
      } catch (err) {
        validationError.value = err
      }
    }

    return {
      topic,
      postContentRef,
      currentUser,
      newPost,
      subscribeTopic,
      subscribePost,
      validationError,
      apiError,
      ...toRefs(postState),
      createPost,
    }
  },
})
</script>
